import { FormEvent, useState } from 'react'
import { Key } from 'react-aria'
import { useNavigate } from 'react-router'
import { Client, GenericObject, ProposalItem } from '../../shared/types'
import { useObjectCreate } from '../api/object'
import { AresModal } from '../components/ares-modal'
import { Button } from '../components/button'
import { ChildObject } from '../components/child-object'
import { Dialog } from '../components/dialog'
import { ProposalVehicleFormatter } from '../components/proposal-vehicle-formatter'
import { Switch } from '../components/switch'
import { useFormLayout } from '../layouts'
import { FormLayout } from '../layouts/form-layout'
import { ObjectsWithConfig } from '../objects/objects'
import { useToggle } from '../use-toggle'

export function Form({
	data,
	onClose,
	redirect,
}: {
	data?: Partial<GenericObject>
	onClose?: VoidFunction
	redirect?: boolean
}) {
	const { layout } = useFormLayout('proposal', 'create')
	const { mutate: create, isPending } = useObjectCreate('proposal')
	const navigate = useNavigate()

	const onSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.stopPropagation()
		e.preventDefault()
		create(new FormData(e.currentTarget), {
			onSuccess(id) {
				onClose?.()
				if (redirect) {
					navigate('/svc/proposal/' + id)
				}
			},
		})
	}

	const [clientId, setClientId] = useState<Key | null>(null)
	const [ico, setIco] = useState('')
	const [icoCallback, setIcoCallback] = useState<(client: Client) => void>()
	const [proposalItems, setProposalItems] = useState<ProposalItem[]>([])
	const [isAdding, toggleAdding] = useToggle()

	const onAdd = (ids: string[], list: GenericObject[]) => {
		setProposalItems(prev => {
			const existing = new Set(prev.map(item => item.id))
			const toAdd = ids
				.filter(item => !existing.has(item))
				.map(item => {
					const vehicle = list.find(pi => pi.id === item)
					return {
						id: item,
						proposal_id: '',
						proposal_vehicle_id: vehicle?.id,
						cena_za_den: vehicle?.cena_za_den,
						cena_za_nadlimitni_km: vehicle?.cena_za_nadlimitni_km,
						nadlimitni_km_v_cene: vehicle?.nadlimitni_km_v_cene,
					}
				}) as ProposalItem[]
			return [...prev, ...toAdd]
		})
		toggleAdding()
	}

	const onRemove = (id: string) => {
		setProposalItems(proposalItems.filter(item => item.proposal_vehicle_id !== id))
	}

	return (
		<>
			{ico && icoCallback ? (
				<AresModal
					ico={ico}
					onClose={() => {
						setIco('')
						setIcoCallback(undefined)
					}}
					onCreate={icoCallback}
				/>
			) : null}

			<form
				className="bg-white rounded-xl border border-slate-200 m-auto max-w-[60em]"
				onSubmit={onSubmit}
			>
				<div className="flex justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
					<h3 className="text-md font-bold">Vytvořit nabídku</h3>
				</div>
				<div className="p-5 space-y-4">
					<ChildObject
						object="client"
						name="Klient"
						identifier="client_id"
						selectedKey={clientId}
						onSelectionChange={setClientId}
						isRequired
						additionalAction={{
							name: 'ARES',
							action: (ico: string, callback: (client: Client) => void) => {
								setIcoCallback(() => callback)
								setIco(ico)
							},
						}}
					/>
					<FormLayout layout={layout} data={data} />
					<div className="flex justify-between items-center gap-4 pt-4">
						<h4 className="text-md font-bold">Vozidla</h4>
						<Button variant="SECONDARY" size="SMALL" onPress={toggleAdding}>
							Přidat
						</Button>
					</div>
					<table className="w-full">
						<thead>
							<tr>
								<th>Vozidlo</th>
								<th>Cena za den</th>
								<th>Cena z km</th>
								<th>Km v ceně</th>
								<th>Akce</th>
							</tr>
						</thead>
						<tbody>
							{proposalItems.map((proposalItem, index) => (
								<tr key={proposalItem.id}>
									<td width="35%">
										<input
											type="hidden"
											name={`vehicle_id[pi${index}]`}
											value={proposalItem.proposal_vehicle_id}
										/>
										<ProposalVehicleFormatter
											id={proposalItem.proposal_vehicle_id}
										/>
									</td>
									<td width="15%">
										<input
											type="text"
											size={8}
											name={`cena_za_den[pi${index}]`}
											defaultValue={proposalItem.cena_za_den as string}
										/>
									</td>
									<td width="15%">
										<input
											type="text"
											size={8}
											name={`cena_za_nadlimitni_km[pi${index}]`}
											defaultValue={
												proposalItem.cena_za_nadlimitni_km as string
											}
										/>
									</td>
									<td width="15%">
										<input
											type="text"
											size={8}
											name={`nadlimitni_km_v_cene[pi${index}]`}
											defaultValue={
												proposalItem.nadlimitni_km_v_cene as string
											}
										/>
									</td>
									<td width="20%">
										<Button
											type="button"
											variant="DANGER"
											size="SMALL"
											onPress={() => {
												onRemove(proposalItem.proposal_vehicle_id)
											}}
										>
											Odebrat
										</Button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<input type="hidden" name="status" value="alternative" />
					<Switch label="Ihned odeslat e-mailem" name="sendEmail" value="1" />
					<Button type="submit" variant="PRIMARY" isPending={isPending}>
						Vytvořit
					</Button>
				</div>
			</form>
			{isAdding ? (
				<Dialog heading="Přidat vozidlo" isOpen onOpenChange={toggleAdding}>
					<div className="flex flex-col overflow-y-auto p-4">
						<div>
							<ObjectsWithConfig
								object="proposal_vehicle"
								name="add-to-proposal"
								defaultSelected={proposalItems.map(
									item => item.proposal_vehicle_id,
								)}
							>
								{({ selected, list }) => (
									<Button variant="PRIMARY" onPress={() => onAdd(selected, list)}>
										Potvrdit
									</Button>
								)}
							</ObjectsWithConfig>
						</div>
					</div>
				</Dialog>
			) : null}
		</>
	)
}
